<script setup>
import { computed, ref } from "vue";
import { useCategoryStore } from "@/stores/useCategoryStore";
import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";

const emit = defineEmits(["close", "refresh", "notify"]);
const categoryStore = useCategoryStore();

// Congela a contagem no momento em que o modal é criado/aberto
const frozenCount = ref(categoryStore.selectedCategoryIds.length);
if (!frozenCount.value || frozenCount.value < 1) frozenCount.value = 1;

const messageTitle = computed(() => {
  if (frozenCount.value > 1) {
    return `Tem certeza que deseja excluir ${frozenCount.value} gêneros?`;
  }
  return "Tem certeza que deseja excluir este gênero?";
});

const messageSubtitle = computed(() => {
  return (
    "Esta ação não pode ser desfeita.\n" +
    "Se algum gênero estiver vinculado a Textos, a exclusão será bloqueada."
  );
});

const successMessage = computed(() => {
  if (frozenCount.value === 1) {
    return "Gênero apagado com sucesso.";
  }

  return `${frozenCount.value} gêneros apagados com sucesso.`;
});

// ===============================
// Tratamento de erro específico (lote)
// ===============================
function buildBatchCategoryDeleteError(error) {
  const foreignKeyError =
    "Erro interno ao remover categorias em lote: Cannot delete or update a parent row: a foreign key constraint fails (`litterae`.`texts`, CONSTRAINT `fk_texts_category1` FOREIGN KEY (`category_id`) REFERENCES `categories` (`category_id`))";

  const userFriendlyFKMessage =
    "Um ou mais gêneros selecionados estão vinculados a Textos e não podem ser removidos.\n" +
    "Para excluí-los, remova todos os textos associados a estes gêneros primeiro.";

  const defaultMessage =
    "Ocorreu um erro ao tentar excluir os gêneros em lote.";

  const raw = error?.response?.data?.message || error?.message || "";

  const looksLikeFK =
    raw.includes("foreign key constraint fails") ||
    raw.toLowerCase().includes("constraint") ||
    raw.toLowerCase().includes("foreign key");

  if (raw.trim() === foreignKeyError.trim() || looksLikeFK) {
    return userFriendlyFKMessage;
  }

  return raw || defaultMessage;
}

async function onConfirmBatchDelete() {
  try {
    await categoryStore.deleteSelectedCategories();

    categoryStore.shouldForcePageOne = true;
    categoryStore.selectedCategoryIds = [];

    emit("notify", {
      type: "success",
      message: successMessage.value,
    });

    emit("refresh");
    emit("close");
  } catch (error) {
    const msg = buildBatchCategoryDeleteError(error);

    emit("notify", {
      type: "error",
      message: msg,
    });

    throw new Error(msg);
  }
}
</script>

<template>
  <DeleteConfirmModal
    :show="true"
    title="Apagar Gêneros em Lote"
    :messageTitle="messageTitle"
    :messageSubtitle="messageSubtitle"
    confirmText="Sim, apagar"
    cancelText="Cancelar"
    loadingText="Apagando..."
    :onConfirm="onConfirmBatchDelete"
    @close="emit('close')"
  />
</template>
